import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { toast } from 'sonner';
import { Building2, ChevronDown, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ROLE_LABELS = {
  is: { owner: 'Eigandi', admin: 'Stjórnandi', manager: 'Verkstjóri', worker: 'Starfsmaður', bookkeeper: 'Bókhaldari' },
  en: { owner: 'Owner', admin: 'Admin', manager: 'Manager', worker: 'Worker', bookkeeper: 'Bookkeeper' },
};

export default function CompanySwitcher({ compact = false }) {
  const { user, memberships, switchCompany } = useAuth();
  const { language } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const dropdownRef = useRef(null);

  const roleLabel = (role) => ROLE_LABELS[language]?.[role] || role;

  const handleSwitch = async (companyId) => {
    if (companyId === user?.company_id || switching) return;
    setSwitching(true);
    try {
      const userData = await switchCompany(companyId);
      setOpen(false);
      toast.success(
        language === 'is'
          ? `Skipt yfir í ${userData.company_name}`
          : `Switched to ${userData.company_name}`
      );
      // Navigate to dashboard to ensure clean state
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSwitching(false);
    }
  };

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Only show if user has 2+ companies
  if (!memberships || memberships.length < 2) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {compact ? (
        <button
          onClick={() => setOpen(!open)}
          className={`w-full flex items-center justify-center p-2 rounded-xl border transition-colors ${
            open ? 'bg-accent/10 border-accent/30' : 'border-border hover:bg-card'
          }`}
          data-testid="company-switcher-btn"
          title={user?.company_name}
        >
          <Building2 size={18} className={open ? 'text-accent' : 'text-foreground-muted'} />
        </button>
      ) : (
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border hover:bg-card transition-colors text-left max-w-[200px]"
          data-testid="company-switcher-btn"
          style={{ minHeight: '36px' }}
        >
          <Building2 size={14} className="text-foreground-muted flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium truncate">{user?.company_name}</p>
            <p className="text-[10px] text-foreground-muted capitalize">{roleLabel(user?.role)}</p>
          </div>
          <ChevronDown size={14} className={`text-foreground-muted flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      )}

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className={`absolute z-50 w-72 bg-card border border-border rounded-xl shadow-xl overflow-hidden ${
              compact ? 'left-full bottom-0 ml-2' : 'top-full left-0 mt-1'
            }`}
            data-testid="company-switcher-dropdown"
          >
            <div className="p-2">
              {memberships.map((m) => {
                const isActive = m.company_id === user?.company_id;
                return (
                  <button
                    key={m.company_id}
                    onClick={() => handleSwitch(m.company_id)}
                    disabled={switching}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                      isActive
                        ? 'bg-accent/10 border border-accent/20'
                        : 'hover:bg-card-hover border border-transparent'
                    } ${switching ? 'opacity-50' : ''}`}
                    data-testid={`company-option-${m.company_id}`}
                  >
                    <Building2 size={16} className={isActive ? 'text-accent' : 'text-foreground-muted'} />
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-medium truncate ${isActive ? 'text-accent' : ''}`}>
                        {m.company_name}
                      </p>
                      <p className="text-[11px] text-foreground-muted capitalize">{roleLabel(m.role)}</p>
                    </div>
                    {isActive && <Check size={16} className="text-accent flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
